import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import OptimizedImage from './OptimizedImage';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'College Reviews', path: '/college-reviews' },
    { name: 'NEET Predictor', path: '/neet-college-predictor' },
    { name: 'Student Connect', path: '/student-connect' },
    { name: 'Blog', path: '/blog' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const resources = [
    { name: 'JEE Guidance', path: '/blog?category=JEE' },
    { name: 'NEET Preparation', path: '/blog?category=NEET' },
    { name: 'College Comparison', path: '/college-reviews' },
    { name: 'Admission Process', path: '/blog?category=Admissions' },
    { name: 'Scholarship Info', path: '/blog?category=Scholarships' }
  ];

  const legalLinks = [
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Refund & Cancellation', path: '/refund-policy' }
  ];

  const socialLinks = [
    { 
      icon: Facebook, 
      href: 'https://www.facebook.com/profile.php?id=61576782135500', 
      color: 'text-primary',
      name: 'Facebook'
    },
    { 
      icon: Twitter, 
      href: 'https://twitter.com/gradvikalp', 
      color: 'text-info',
      name: 'Twitter'
    },
    { 
      icon: Instagram, 
      href: 'https://instagram.com/gradvikalp', 
      color: 'text-danger',
      name: 'Instagram'
    },
    { 
      icon: Linkedin, 
      href: 'https://www.linkedin.com/company/grad-vikalp/', 
      color: 'text-primary',
      name: 'LinkedIn'
    }
  ];

  return (
    <footer className="footer-bg text-white py-5">
      <div className="container px-3 px-md-4">
        <div className="row g-4 g-md-5">
          {/* Company Info */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="d-flex align-items-center mb-4">
              <OptimizedImage
                src="/Grad-Vikalp.png" 
                alt="Grad Vikalp Logo - Your College Guidance Partner"
                title="Grad Vikalp - Find Your Dream College"
                style={{ height: 'clamp(40px, 8vw, 50px)', width: 'auto' }}
                loading="lazy"
              />
            </div>
            <p className="text-light mb-4" style={{ maxWidth: '400px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}>
              Your trusted partner in finding the perfect college. We connect students with verified reviews, 
              personalized recommendations, and real student experiences to make informed decisions.
            </p>
            
            <div className="vstack gap-2 gap-md-3">
              <div className="d-flex align-items-center gap-2 text-light" style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>
                <Mail size={16} />
                <a href="mailto:vikalpgrad@gmail.com" className="text-light text-decoration-none">
                  vikalpgrad@gmail.com
                </a>
              </div>
              <div className="d-flex align-items-center gap-2 text-light" style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>
                <Phone size={16} />
                <a href="tel:+917568377278" className="text-light text-decoration-none">
                  +91 7568377278
                </a>
              </div>
              <div className="d-flex align-items-center gap-2 text-light" style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>
                <MapPin size={16} />
                <span>Jaipur, India</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-6 col-md-3 col-lg-2">
            <h5 className="fw-semibold mb-3 mb-md-4" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Quick Links</h5>
            <ul className="list-unstyled">
              {quickLinks.map((link) => (
                <li key={link.name} className="mb-2">
                  <Link 
                    to={link.path} 
                    className="text-light text-decoration-none hover-link"
                    style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="col-6 col-md-3 col-lg-3">
            <h5 className="fw-semibold mb-3 mb-md-4" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Resources</h5>
            <ul className="list-unstyled">
              {resources.map((resource) => (
                <li key={resource.name} className="mb-2">
                  <Link 
                    to={resource.path} 
                    className="text-light text-decoration-none hover-link"
                    style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                  >
                    {resource.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Social */}
          <div className="col-12 col-md-6 col-lg-3">
            <h5 className="fw-semibold mb-3 mb-md-4" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Legal</h5>
            <ul className="list-unstyled mb-4">
              {legalLinks.map((link) => (
                <li key={link.name} className="mb-2">
                  <Link 
                    to={link.path} 
                    className="text-light text-decoration-none hover-link"
                    style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h6 className="fw-semibold mb-3" style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)' }}>Follow Us</h6>
            <div className="d-flex gap-2 gap-md-3 justify-content-center justify-content-md-start">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`social-icon ${social.color}`}
                    style={{ width: 'clamp(2rem, 6vw, 2.5rem)', height: 'clamp(2rem, 6vw, 2.5rem)' }}
                    title={social.name}
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <hr className="my-4 my-md-5 border-secondary" />

        <div className="row align-items-center text-center text-md-start">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <div className="text-light" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}>
              © 2025 Grad Vikalp. All rights reserved.
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="d-flex justify-content-center justify-content-md-end flex-wrap gap-2 gap-md-3 text-light" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}>
              <Link to="/terms" className="text-light text-decoration-none hover-link">
                Terms of Service
              </Link>
              <span>•</span>
              <Link to="/privacy" className="text-light text-decoration-none hover-link">
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/refund-policy" className="text-light text-decoration-none hover-link">
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hover-link {
          transition: all 0.3s ease;
        }
        
        .hover-link:hover {
          color: #60a5fa !important;
          text-decoration: underline !important;
        }
        
        .social-icon {
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background-color: #374151;
          color: #9ca3af;
          transition: all 0.3s ease;
          text-decoration: none;
        }
        
        .social-icon:hover {
          background-color: #4f46e5;
          color: white;
          transform: translateY(-2px);
        }
      `}</style>
    </footer>
  );
};

export default Footer;